import type { TaxonomyNode } from '@/api/generatedApi';
import { useGetTaxonomyChildren } from '@/hooks/api/taxonomy/useGetTaxonomyChildren';
import { useInfiniteTreeScroll } from '@/hooks/useInfiniteTreeScroll';
import { LoadingIndicator } from '@/pages/TaxonomyPage/TaxonomyTree/LoadingIndicator';
import { memo, useEffect, useMemo } from 'react';
import { PickerTreeItem } from './PickerTreeItem';

const PickerNodeLevelComponent = ({
  taxonomyId,
  parentNodeId,
  depth,
  expandedSet,
  currentNodeId,
  ancestorPath,
  scrollContainerRef,
  onSelect,
  onRemove,
  searchMatchIds,
  searchVisibleIds,
}: {
  taxonomyId: string;
  parentNodeId: string;
  depth: number;
  expandedSet: Set<string>;
  currentNodeId: string | undefined;
  ancestorPath: string[] | undefined;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  onSelect: (node: TaxonomyNode, depth: number) => void;
  onRemove: (() => void) | undefined;
  searchMatchIds?: Set<string>;
  searchVisibleIds?: Set<string>;
}) => {
  const { nodes, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetTaxonomyChildren(taxonomyId, parentNodeId);

  const { sentinelRef } = useInfiniteTreeScroll(scrollContainerRef, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const targetChildId = ancestorPath?.[depth];
  const targetLoaded = !targetChildId || nodes.some(n => n.id === targetChildId);

  useEffect(() => {
    if (!targetLoaded && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [targetLoaded, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const visibleNodes = useMemo(
    () => (searchVisibleIds ? nodes.filter(n => searchVisibleIds.has(n.id)) : nodes),
    [nodes, searchVisibleIds]
  );

  return (
    <>
      {visibleNodes.map(node => (
        <PickerTreeItem
          key={node.id}
          node={node}
          depth={depth}
          taxonomyId={taxonomyId}
          expandedSet={expandedSet}
          currentNodeId={currentNodeId}
          ancestorPath={ancestorPath}
          scrollContainerRef={scrollContainerRef}
          onSelect={onSelect}
          onRemove={onRemove}
          searchMatchIds={searchMatchIds}
          searchVisibleIds={searchVisibleIds}
        />
      ))}
      {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
      {isFetchingNextPage && <LoadingIndicator count={2} />}
    </>
  );
};

export const PickerNodeLevel = memo(PickerNodeLevelComponent);
